import { DateAsString, ForsikringDekningDTO, SporsmalId, SporsmalType, SvarId } from '../../types/generated-types';
import { Kundeinfo } from '../../types/internal-types';
import { isSkadedatoSporsmal } from '../dato/dato-types';
import { isKontaktinfoSporsmal } from '../kontaktinfo/kontaktinfo-types';
import { isRadioSporsmal } from '../radio/radio-types';
import { isBesvartSporsmalSide } from '../sporsmalSide/sporsmalSide-types';
import { earliestUtleggDato, isUtleggSporsmal } from '../utlegg/utlegg-types';
import {
    isSporsmalsGruppeTriggeredByUtsattForgreining,
    SporsmalsGruppeTriggeredByUtsattForgreining,
} from '../utsattForgreining/utsatt-forgreining-types';
import { validateFodselsnummer, validateOrganisasjonsnummer } from '../validation/validate';
import {
    addSporsmalToBesvarteSporsmal,
    BesvartSporsmal,
    copyNextSvarToCurrentSporsmal,
    CurrentSporsmal,
    currentSporsmalIsEmpty,
    currentSporsmalSteg,
    CurrentSteg,
    CurrentStegTypes,
    flattenBesvarteSporsmal,
    isSporsmalSteg,
    oppsummeringSteg,
    toBesvartSporsmal,
    toCurrentSporsmal,
} from './current-steg';
import { CurrentSporsmalAction, currentSporsmalReducer } from './currentSporsmalReducer';
import { SporsmalsflytActionKeys } from './sporsmalsflyt-actions';
import { IdentifikatorType, Sporsmal, SporsmalsGruppe, UtsattForgreiningsporsmal } from './sporsmalsflyt-types';
import { canTriggerNewSporsmal, isTriggeredBySporsmal, TriggeredBy, triggeredByEquals } from './triggeredby-types';

export interface ForsikringIdentifikator {
    forsikringsnummer: string;
    forsikringsversjon: number;
}

export interface SporsmalsflytState {
    besvarteSporsmal: BesvartSporsmal[];
    currentSteg: CurrentSteg;
    next: CurrentSporsmal[];
    forsikringIdentifikator: ForsikringIdentifikator | null;
}

export const initializeSporsmalsflytState = (sporsmalListe: Sporsmal[]): SporsmalsflytState => ({
    besvarteSporsmal: [],
    currentSteg: currentSporsmalSteg(toCurrentSporsmal(sporsmalListe[0])),
    next: [],
    forsikringIdentifikator: null,
});

export const isFirstSporsmal = (besvarteSporsmal: BesvartSporsmal[]) => besvarteSporsmal.length === 0;

const currentStegIsEmpty = (currentSteg: CurrentSteg): boolean => {
    if (isSporsmalSteg(currentSteg)) {
        return currentSporsmalIsEmpty(currentSteg.currentSporsmal);
    }
    return true;
};

export const sporsmalsflytStateIsEmpty = (sporsmalsflytState: SporsmalsflytState): boolean =>
    sporsmalsflytState.besvarteSporsmal.length === 0 &&
    currentStegIsEmpty(sporsmalsflytState.currentSteg) &&
    sporsmalsflytState.next.every(currentSporsmalIsEmpty);

export const updateForsikringIdentifierIfForsikringsporsmal = (
    forsikringIdentifikator: ForsikringIdentifikator | null,
    action: CurrentSporsmalAction,
): ForsikringIdentifikator => {
    switch (action.type) {
        case SporsmalsflytActionKeys.UpdateForsikringSvar:
        case SporsmalsflytActionKeys.UpdateForsikringInSkadedatoAndForsikringSvar:
            if (action.selectedAlternativ.id === ForsikringDekningDTO.FinnerIkkeRiktig) return null;
            return {
                forsikringsnummer: action.selectedAlternativ.forsikringsnummer,
                forsikringsversjon: action.selectedAlternativ.forsikringversjon,
            };
        case SporsmalsflytActionKeys.UpdateSporsmalSide:
            return updateForsikringIdentifierIfForsikringsporsmal(forsikringIdentifikator, action.action);
        default:
            return forsikringIdentifikator;
    }
};

/*
 * Updating currentSporsmal
 */
export const updateCurrentSporsmal = (
    sporsmalsflytState: SporsmalsflytState,
    action: CurrentSporsmalAction = {} as CurrentSporsmalAction,
): SporsmalsflytState => {
    if (isSporsmalSteg(sporsmalsflytState.currentSteg)) {
        return {
            ...sporsmalsflytState,
            currentSteg: {
                ...sporsmalsflytState.currentSteg,
                currentSporsmal: currentSporsmalReducer(
                    sporsmalsflytState.currentSteg.currentSporsmal,
                    sporsmalsflytState.besvarteSporsmal,
                    action,
                ),
            },
            forsikringIdentifikator: updateForsikringIdentifierIfForsikringsporsmal(
                sporsmalsflytState.forsikringIdentifikator,
                action,
            ),
        };
    }
    return sporsmalsflytState;
};

/*
 * Navigation
 */

const indexOf = (sporsmalId: string, sporsmals: Sporsmal[]) =>
    sporsmals.findIndex((sporsmal) => sporsmal.id === sporsmalId);

const sporsmalAfter = (sporsmalId: string, sporsmals: Sporsmal[]) => {
    const index = indexOf(sporsmalId, sporsmals);
    if (index >= 0 && index + 1 < sporsmals.length) {
        return sporsmals[index + 1];
    }

    return undefined;
};

const nextSporsmalsGruppeTriggered = (
    currentSporsmal: CurrentSporsmal,
    triggered: SporsmalsGruppe[],
): SporsmalsGruppe | undefined =>
    triggered.find((sporsmalsGruppe) => isTriggeredBySporsmal(sporsmalsGruppe.triggeredBy, currentSporsmal));

const findTriggeredSporsmalsGruppe = (triggeredBy: TriggeredBy, triggered: SporsmalsGruppe[]) =>
    triggered.find((gruppe) => triggeredByEquals(gruppe.triggeredBy, triggeredBy));

const findNextSporsmalInListe = (
    sporsmalList: Sporsmal[],
    triggered: SporsmalsGruppe[],
    currentSporsmal: CurrentSporsmal,
) => {
    if (canTriggerNewSporsmal(currentSporsmal.sporsmal)) {
        const sporsmalsGruppeTriggered: SporsmalsGruppe = nextSporsmalsGruppeTriggered(currentSporsmal, triggered);
        if (sporsmalsGruppeTriggered) {
            return sporsmalsGruppeTriggered.sporsmalsList[0];
        }
    }

    return sporsmalAfter(currentSporsmal.sporsmal.id, sporsmalList);
};

const findNextSporsmalInSporsmalsGruppe = (currentSporsmal: CurrentSporsmal, triggered: SporsmalsGruppe[]) => {
    const sporsmalsGruppe = findTriggeredSporsmalsGruppe(currentSporsmal.sporsmal.triggeredBy, triggered);
    return findNextSporsmalInListe(sporsmalsGruppe.sporsmalsList, triggered, currentSporsmal);
};

const findParentSporsmalsGruppeWithSporsmal = (sporsmalsId: string, triggered: SporsmalsGruppe[]) =>
    triggered.find((sporsmalsGruppe) =>
        sporsmalsGruppe.sporsmalsList.some((sporsmal: Sporsmal) => sporsmal.id === sporsmalsId),
    );

const backTrackToNextRegularSporsmal = (
    triggeredBy: TriggeredBy,
    hovedflyt: Sporsmal[],
    sporsmalsGruppeList: SporsmalsGruppe[],
): Sporsmal => {
    const foundSporsmalsGruppe = findParentSporsmalsGruppeWithSporsmal(triggeredBy.sporsmalId, sporsmalsGruppeList);
    if (foundSporsmalsGruppe) {
        const nextSporsmal = sporsmalAfter(triggeredBy.sporsmalId, foundSporsmalsGruppe.sporsmalsList);
        if (nextSporsmal) {
            return nextSporsmal;
        }
        return backTrackToNextRegularSporsmal(foundSporsmalsGruppe.triggeredBy, hovedflyt, sporsmalsGruppeList);
    }

    return sporsmalAfter(triggeredBy.sporsmalId, hovedflyt);
};

const getNextSporsmal = (
    sporsmalsList: Sporsmal[],
    sporsmalsGruppeList: SporsmalsGruppe[],
    currentSporsmal: CurrentSporsmal,
): Sporsmal | undefined => {
    const isInTriggeredSporsmalsGruppe = currentSporsmal.sporsmal.triggeredBy;
    if (isInTriggeredSporsmalsGruppe) {
        const nextSporsmal = findNextSporsmalInSporsmalsGruppe(currentSporsmal, sporsmalsGruppeList);

        if (nextSporsmal) {
            return nextSporsmal;
        }
        return backTrackToNextRegularSporsmal(currentSporsmal.sporsmal.triggeredBy, sporsmalsList, sporsmalsGruppeList);
    }

    return findNextSporsmalInListe(sporsmalsList, sporsmalsGruppeList, currentSporsmal);
};

export const getNextSteg = (
    sporsmalsList: Sporsmal[],
    sporsmalsGruppeList: SporsmalsGruppe[],
    besvarteSporsmal: BesvartSporsmal[],
    currentSporsmal: CurrentSporsmal,
): Sporsmal | undefined => {
    const nextSporsmal = getNextSporsmal(sporsmalsList, sporsmalsGruppeList, currentSporsmal);

    if (nextSporsmal?.type === SporsmalType.UtsattForgreining) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        return findSporsmalThatIsTriggeredByUtsattForgreining(
            nextSporsmal,
            besvarteSporsmal,
            sporsmalsList,
            sporsmalsGruppeList,
        );
    }
    if (nextSporsmal) {
        return nextSporsmal;
    }
    return undefined;
};

const isBedriftBesvart = (besvartSporsmal: BesvartSporsmal) =>
    isRadioSporsmal(besvartSporsmal) &&
    besvartSporsmal.sporsmal.id === SporsmalId.BedriftEllerPrivat &&
    besvartSporsmal.svarId === SvarId.BedriftEllerPrivat_Alternativ_Bedrift;

const hasKundeBesvartThatSkadeIsMeldtPaVegneAvBedrift = (besvarteSporsmal: BesvartSporsmal[]): boolean =>
    besvarteSporsmal.some(
        (besvartSporsmal) =>
            isBedriftBesvart(besvartSporsmal) ||
            (isBesvartSporsmalSide(besvartSporsmal) &&
                besvartSporsmal.besvarteSporsmal.some((besvartSporsmal) => isBedriftBesvart(besvartSporsmal))),
    );

export const getIdentifikatorType = (besvarteSporsmal: BesvartSporsmal[], offentligId?: string): IdentifikatorType =>
    hasKundeBesvartThatSkadeIsMeldtPaVegneAvBedrift(besvarteSporsmal) ||
    (offentligId && validateOrganisasjonsnummer(offentligId))
        ? IdentifikatorType.Organisasjonsnummer
        : IdentifikatorType.Fodselsnummer;

export const findSkadeDato = (besvarteSporsmal: BesvartSporsmal[]): DateAsString | undefined => {
    const flatMappedBesvarteSporsmal = flattenBesvarteSporsmal(besvarteSporsmal);
    const skadeDatoSporsmal = flatMappedBesvarteSporsmal.find(
        (besvartSporsmal) => isSkadedatoSporsmal(besvartSporsmal) || isUtleggSporsmal(besvartSporsmal),
    );

    if (isSkadedatoSporsmal(skadeDatoSporsmal)) {
        return skadeDatoSporsmal.dato;
    }
    if (isUtleggSporsmal(skadeDatoSporsmal)) {
        return earliestUtleggDato(skadeDatoSporsmal);
    }
    return undefined;
};

const nextSporsmalHasAlreadyBeenVisited = (currentSporsmal: CurrentSporsmal, next: CurrentSporsmal[]) =>
    currentSporsmal.sporsmal.id === next[0]?.sporsmal.id;

const updateSporsmalsflytState = (
    currentSporsmal: CurrentSporsmal,
    identifikatorType: IdentifikatorType,
    skadeDato: DateAsString,
    kundeinfo?: Kundeinfo,
) => {
    if (isKontaktinfoSporsmal(currentSporsmal)) {
        if (kundeinfo) {
            return {
                ...currentSporsmal,
                identifikatorType,
                showFodselsnummer:
                    identifikatorType === IdentifikatorType.Fodselsnummer &&
                    !validateFodselsnummer(kundeinfo.offentligId),
            };
        }
        return {
            ...currentSporsmal,
            identifikatorType,
            showFodselsnummer: identifikatorType === IdentifikatorType.Fodselsnummer,
        };
    }

    // TODO må vi ha en oppdatering av forsikringer her??

    return currentSporsmal;
};

const findSporsmalsGruppeTriggeredByUtsattForgreining = (
    triggered: SporsmalsGruppe[],
    utsattForgreining: UtsattForgreiningsporsmal,
): SporsmalsGruppeTriggeredByUtsattForgreining =>
    triggered
        .filter((sporsmalsgruppe) => sporsmalsgruppe.triggeredBy.sporsmalId === utsattForgreining.id)
        .find(isSporsmalsGruppeTriggeredByUtsattForgreining);

export const findSporsmalThatIsTriggeredByUtsattForgreining = (
    utsattForgreining: UtsattForgreiningsporsmal,
    besvarteSporsmal: BesvartSporsmal[],
    sporsmalList: Sporsmal[],
    triggered: SporsmalsGruppe[],
): Sporsmal | undefined => {
    const sporsmalsGruppe = findSporsmalsGruppeTriggeredByUtsattForgreining(triggered, utsattForgreining);

    const besvartSporsmalMatchesTrigger = besvarteSporsmal.some((besvartSporsmal) =>
        sporsmalsGruppe.triggeredBy.triggers.some((trigger: TriggeredBy) =>
            isTriggeredBySporsmal(trigger, besvartSporsmal),
        ),
    );

    if (besvartSporsmalMatchesTrigger) {
        return sporsmalsGruppe.sporsmalsList[0];
    }

    return getNextSteg(sporsmalList, triggered, besvarteSporsmal, toCurrentSporsmal(utsattForgreining));
};

export const nextStep = (
    sporsmalsflytState: SporsmalsflytState,
    sporsmalList: Sporsmal[],
    triggered: SporsmalsGruppe[],
    kundeinfo?: Kundeinfo,
): SporsmalsflytState => {
    if (isSporsmalSteg(sporsmalsflytState.currentSteg)) {
        const besvartSporsmal = toBesvartSporsmal(sporsmalsflytState.currentSteg.currentSporsmal);
        if (besvartSporsmal) {
            const besvarteSporsmal = addSporsmalToBesvarteSporsmal(
                besvartSporsmal,
                sporsmalsflytState.besvarteSporsmal,
            );

            const nextSporsmal: Sporsmal | undefined = getNextSteg(
                sporsmalList,
                triggered,
                besvarteSporsmal,
                sporsmalsflytState.currentSteg.currentSporsmal,
            );

            if (!nextSporsmal) {
                return {
                    ...sporsmalsflytState,
                    besvarteSporsmal,
                    currentSteg: oppsummeringSteg(),
                    next: [],
                };
            }

            const currentSporsmal = toCurrentSporsmal(
                nextSporsmal,
                besvarteSporsmal,
                kundeinfo,
                getIdentifikatorType(besvarteSporsmal, kundeinfo?.offentligId),
                findSkadeDato(besvarteSporsmal),
            );

            if (nextSporsmalHasAlreadyBeenVisited(currentSporsmal, sporsmalsflytState.next)) {
                return {
                    ...sporsmalsflytState,
                    besvarteSporsmal,
                    currentSteg: currentSporsmalSteg(
                        updateSporsmalsflytState(
                            copyNextSvarToCurrentSporsmal(
                                currentSporsmal,
                                sporsmalsflytState.next[0],
                                besvarteSporsmal,
                            ),
                            getIdentifikatorType(besvarteSporsmal, kundeinfo?.offentligId),
                            findSkadeDato(besvarteSporsmal),
                            kundeinfo,
                        ),
                    ),
                    next: sporsmalsflytState.next.slice(1),
                };
            }

            return {
                ...sporsmalsflytState,
                besvarteSporsmal,
                currentSteg: currentSporsmalSteg(currentSporsmal),
                next: [],
            };
        }
    }

    return sporsmalsflytState;
};

const getPreviousSporsmal = (besvarteSporsmal: BesvartSporsmal[]): BesvartSporsmal | undefined =>
    besvarteSporsmal[besvarteSporsmal.length - 1];

export const previousStep = (sporsmalsflytState: SporsmalsflytState): SporsmalsflytState => {
    const previousSporsmal: BesvartSporsmal | undefined = getPreviousSporsmal(sporsmalsflytState.besvarteSporsmal);
    if (previousSporsmal) {
        return {
            ...sporsmalsflytState,
            currentSteg: currentSporsmalSteg(previousSporsmal),
            besvarteSporsmal: sporsmalsflytState.besvarteSporsmal.slice(0, -1),
            next: isSporsmalSteg(sporsmalsflytState.currentSteg)
                ? [sporsmalsflytState.currentSteg.currentSporsmal, ...sporsmalsflytState.next]
                : sporsmalsflytState.next,
        };
    }
    return sporsmalsflytState;
};

export const updateWithBesvarteSporsmalFromCache = (
    sporsmalsflytState: SporsmalsflytState,
    besvarteSporsmal: BesvartSporsmal[],
): SporsmalsflytState => {
    return {
        ...sporsmalsflytState,
        besvarteSporsmal: [],
        next: besvarteSporsmal.slice(1),
        currentSteg: { type: CurrentStegTypes.Sporsmal, currentSporsmal: besvarteSporsmal[0] },
    };
};
